import './Translations.scss';
import { RouteComponentProps } from 'react-router';
import React, { useState } from 'react';
import PageWrapper from '@go-client/pages/PageWrapper';
import Page from '@go-client/pages/Page';
import { MenuBar } from '@go-client/components';
import { BrandLogo } from '@go-client/components/UIkit/Brand/BrandLogo/BrandLogo';
import { PropertyBanner } from '@go-client/components/UIkit/Property/PropertyBanner';
import {
  BubbleOverlay,
  BubbleAccordion,
  BubbleTypeahead,
  BubbleFormGroup,
  BubbleInput,
} from '@go-client/components/BubbleUI';
import { Property } from '@go-shared/types/property';
import {
  BubbleTypeaheadValue,
  BubbleTypeaheadOption,
} from '@go-client/components/BubbleUI/BubbleTypeahead';
import { Language } from '@go-shared/types/info';
import { mapBy, notUndefined } from '@go-shared/lib/functional';
import uuid from 'uuid';
import { CommonTranslationFilters, TranslationFilters } from '@go-shared/lib/translation';
import TranslationsNavigation from '@go-client/components/Navigations/TranslationsNavigation';
import { TRANSLATIONS_SUBNAV_TYPE } from '@go-client/routes';

interface TranslationPagePropsBase {
  property: Property;
  languages: Language[];
  showLibraryFilters?: boolean;
  loading?: boolean;
  contentRef?: React.MutableRefObject<HTMLDivElement | null>;
  category: TRANSLATIONS_SUBNAV_TYPE;
  leftFn?: (props: TranslationFilters) => React.ReactNode;
  rightFn?: (props: TranslationFilters) => React.ReactNode;
  headerFn?: (props: TranslationFilters) => React.ReactNode;
  sideFn?: (props: TranslationFilters) => React.ReactNode;
  outerFn?: (props: TranslationFilters) => React.ReactNode;
  filters?: CommonTranslationFilters;
  setFilters?: (props: CommonTranslationFilters) => void;
}

interface TranslationFiltersComplex {
  selectedLanguages: Array<BubbleTypeaheadValue<Language>>;
}

type TranslationsPageProps = TranslationPagePropsBase & RouteComponentProps;

export default function Translations(props: TranslationsPageProps) {
  const {
    property,
    languages,
    contentRef,
    leftFn,
    rightFn,
    headerFn,
    sideFn,
    outerFn,
    loading,
    showLibraryFilters,
    category,
    filters,
    setFilters,
    ...rest
  } = props;

  const languageOpts: Array<BubbleTypeaheadOption<Language>> = languages.map(lang => ({
    key: lang.key,
    item: lang,
  }));

  const languageOptsMap = mapBy(languageOpts, 'key');

  const [languagesFilter, setLanguagesFilter] = useState<TranslationFiltersComplex>({
    selectedLanguages: languageOpts
      .map(lopt => {
        const refOpt = languageOptsMap[lopt.key];
        return refOpt
          ? {
              key: uuid(),
              item: lopt.item,
              refKey: refOpt.key,
            }
          : undefined;
      })
      .filter(notUndefined),
  });

  const showFilters = showLibraryFilters && filters && setFilters;

  const renderFnProps: TranslationFilters = {
    ...filters,
    selectedLanguages: languagesFilter.selectedLanguages.map(sl => sl.item),
  };

  function handleOnAddLanguage(value: BubbleTypeaheadValue<Language>) {
    const newLanguages = [...languagesFilter.selectedLanguages, value];
    setLanguagesFilter({ ...languagesFilter, selectedLanguages: newLanguages });
  }

  function handleOnDeleteLanguage(value: BubbleTypeaheadValue<Language>) {
    const newLanguages = [...languagesFilter.selectedLanguages].filter(
      val => val.key !== value.key
    );
    setLanguagesFilter({ ...languagesFilter, selectedLanguages: newLanguages });
  }

  return (
    <PageWrapper {...rest}>
      <Page className="Translations absolute flex items-stretch">
        <MenuBar {...rest} property={property} />
        <div className="SideMenu flex flex-col bg-titanium-darkest shadow-md">
          <div className="bg-white flex flex-col items-center">
            <BrandLogo logo={property.brand.logo} />
            <PropertyBanner {...rest} property={property} swappable />
          </div>
          {sideFn && sideFn(renderFnProps)}
          <BubbleAccordion
            variant="titanium"
            className="flex-grow flex flex-col border-t border-titanium-darkest"
            labels={['Filters']}
            open={[0]}
            locked={[0]}
          >
            <div className="grid gap-4 p-4">
              <BubbleFormGroup label="Language" labelClassName="text-white-i">
                <BubbleTypeahead
                  block
                  variant="titanium"
                  labelExtractor={el => el.englishLabel}
                  placeholder="Filter languages shown..."
                  menuClassName="max-h-80 overflow-y-auto"
                  value={languagesFilter.selectedLanguages}
                  options={languageOpts}
                  onAdd={handleOnAddLanguage}
                  onRemove={handleOnDeleteLanguage}
                />
              </BubbleFormGroup>
              {showFilters && (
                <>
                  <BubbleFormGroup label="Created Between" labelClassName="text-white-i">
                    <div className="grid grid-cols-1 gap-1">
                      <BubbleInput
                        variant="titanium"
                        type="date"
                        value={filters.createdFrom}
                        onValue={date => setFilters({ ...filters, page: 1, createdFrom: date })}
                      />
                      <BubbleInput
                        variant="titanium"
                        type="date"
                        value={filters.createdTo}
                        onValue={date => setFilters({ ...filters, page: 1, createdTo: date })}
                      />
                    </div>
                  </BubbleFormGroup>
                  <BubbleFormGroup label="Published" labelClassName="text-white-i">
                    <div className="grid grid-cols-3 gap-1 align-left">
                      <div className="text-white relative">
                        <BubbleInput
                          variant="titanium"
                          type="checkbox"
                          appearance="rounded"
                          inputClassName="stretched-link"
                          className="mr-1"
                          checked={filters.published === undefined}
                          onValue={() => setFilters({ ...filters, page: 1, published: undefined })}
                        />
                        <span>Any</span>
                      </div>
                      <div className="text-white relative">
                        <BubbleInput
                          variant="titanium"
                          type="checkbox"
                          appearance="rounded"
                          inputClassName="stretched-link"
                          className="mr-1"
                          checked={filters.published === true}
                          onValue={() => setFilters({ ...filters, page: 1, published: true })}
                        />
                        <span>Yes</span>
                      </div>
                      <div className="text-white relative">
                        <BubbleInput
                          variant="titanium"
                          type="checkbox"
                          appearance="rounded"
                          inputClassName="stretched-link"
                          className="mr-1"
                          checked={filters.published === false}
                          onValue={() => setFilters({ ...filters, page: 1, published: false })}
                        />
                        <span>No</span>
                      </div>
                    </div>
                  </BubbleFormGroup>
                  <BubbleFormGroup label="Translated" labelClassName="text-white-i">
                    <div className="grid grid-cols-3 gap-1 align-left">
                      <div className="text-white relative">
                        <BubbleInput
                          variant="titanium"
                          type="checkbox"
                          appearance="rounded"
                          inputClassName="stretched-link"
                          className="mr-1"
                          checked={filters.translated === undefined}
                          onValue={() => setFilters({ ...filters, page: 1, translated: undefined })}
                        />
                        <span>Any</span>
                      </div>
                      <div className="text-white relative">
                        <BubbleInput
                          variant="titanium"
                          type="checkbox"
                          appearance="rounded"
                          inputClassName="stretched-link"
                          className="mr-1"
                          checked={filters.translated === true}
                          onValue={() => setFilters({ ...filters, translated: true })}
                        />
                        <span>Yes</span>
                      </div>
                      <div className="text-white relative">
                        <BubbleInput
                          variant="titanium"
                          type="checkbox"
                          appearance="rounded"
                          inputClassName="stretched-link"
                          className="mr-1"
                          checked={filters.translated === false}
                          onValue={() => setFilters({ ...filters, page: 1, translated: false })}
                        />
                        <span>No</span>
                      </div>
                    </div>
                  </BubbleFormGroup>
                </>
              )}
            </div>
          </BubbleAccordion>
        </div>
        <div
          ref={contentRef}
          className="TranslationsContent bg-blue-lightest overflow-y-auto flex-grow"
        >
          <div
            className="TranslationsHeader align-with-logo flex items-end px-5 sticky top-0
              left-0 z-10 bg-blue-lightest"
          >
            <div className="mb-3 pl-3 flex-grow flex items-end">
              <TranslationsNavigation active={category} />
            </div>
            <div
              className="absolute w-full bg-blue-lightest flex border-blue-lightest -mx-5 px-5"
              style={{
                height: 'calc(40px + 0.75rem)',
                bottom: 'calc(-40px - 0.75rem)',
                borderBottomWidth: '0.75rem',
                zIndex: 10,
              }}
            >
              {headerFn && headerFn(renderFnProps)}
            </div>
          </div>
          <div className="TranslationsBody mx-5 content-minus-logo min-w-900">
            <div className="flex flex-col">
              {loading ? (
                <BubbleOverlay
                  loading
                  style={{ minHeight: '200px', marginTop: 'calc(40px + 0.75rem)' }}
                  className="shadow rounded"
                />
              ) : (
                <>
                  <div className="grid grid-cols-4 gap-4 items-start flex-grow">
                    <div className="bg-white shadow-md rounded overflow-clip">
                      {leftFn && leftFn(renderFnProps)}
                    </div>
                    <div
                      className="sticky col-span-3"
                      style={{
                        top: 'calc(130px + 40px + 0.75rem)',
                        marginTop: 'calc(40px + 0.75rem)',
                        height: 'calc(100vh - 130px - 2rem - 40px - 0.75rem - 1px)',
                      }}
                    >
                      <div className="bg-white shadow-md max-h-full flex flex-col rounded overflow-clip">
                        <div
                          className="flex-grow overflow-auto"
                          style={{ maxHeight: 'calc(100% - 50px)' }}
                        >
                          {rightFn && rightFn(renderFnProps)}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3">&nbsp;</div>
                </>
              )}
            </div>
          </div>
        </div>
        {outerFn && outerFn(renderFnProps)}
      </Page>
    </PageWrapper>
  );
}
